import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useTriggerWidth } from '../hooks/use-trigger-width';
import { typographyVariant } from '../typography';
import { popoverContentClasses } from './popover.styles';
import type { PopoverContentProps } from './popover.types';

type PopoverTriggerContextValue = {
  triggerEl: HTMLElement | null;
  setTriggerEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
};

const PopoverTriggerContext = React.createContext<PopoverTriggerContextValue | null>(
  null
);

const Popover = ({
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) => {
  const [triggerEl, setTriggerEl] = React.useState<HTMLElement | null>(null);
  const triggerContext = React.useMemo(() => ({ triggerEl, setTriggerEl }), [triggerEl]);

  return (
    <PopoverTriggerContext.Provider value={triggerContext}>
      <PopoverPrimitive.Root data-slot="popover" {...props}>
        {children}
      </PopoverPrimitive.Root>
    </PopoverTriggerContext.Provider>
  );
};

const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>((props, ref) => {
  const triggerContext = React.useContext(PopoverTriggerContext);
  const composedRef = useComposedRefs(ref, triggerContext?.setTriggerEl);

  return (
    <PopoverPrimitive.Trigger ref={composedRef} data-slot="popover-trigger" {...props} />
  );
});
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

const PopoverAnchor = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Anchor>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Anchor>
>((props, ref) => (
  <PopoverPrimitive.Anchor ref={ref} data-slot="popover-anchor" {...props} />
));
PopoverAnchor.displayName = PopoverPrimitive.Anchor.displayName;

const PopoverClose = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Close>
>((props, ref) => (
  <PopoverPrimitive.Close ref={ref} data-slot="popover-close" {...props} />
));
PopoverClose.displayName = PopoverPrimitive.Close.displayName;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    { className, align = 'center', sideOffset = 4, matchTriggerWidth, style, ...props },
    ref
  ) => {
    const triggerContext = React.useContext(PopoverTriggerContext);
    // Wait for mounted content so Slot's open render settles the trigger ref.
    const [contentEl, setContentEl] = React.useState<HTMLElement | null>(null);
    const composedRef = useComposedRefs(ref, setContentEl);
    const triggerWidth = useTriggerWidth(
      matchTriggerWidth && contentEl ? (triggerContext?.triggerEl ?? null) : null
    );
    const collisionSafeTriggerWidth =
      triggerWidth === undefined
        ? undefined
        : `min(${triggerWidth}px, var(--radix-popper-available-width, ${triggerWidth}px))`;

    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={matchTriggerWidth ? composedRef : ref}
          align={align}
          data-slot="popover-content"
          className={cn(popoverContentClasses, className)}
          // Cap the wrapper's `min-width: max-content` at both the trigger and
          // Radix collision space, so narrow viewports can still shrink it.
          style={
            triggerWidth === undefined
              ? style
              : {
                  width: triggerWidth,
                  maxWidth: collisionSafeTriggerWidth,
                  ...style,
                }
          }
          sideOffset={sideOffset}
          /**
           * Fix for issue with scrolling:
           * @see https://github.com/shadcn-ui/ui/issues/607
           */
          onWheel={(e) => {
            e.stopPropagation();

            const isScrollingDown = e.deltaY > 0;

            if (isScrollingDown) {
              // Simulate arrow down key press
              e.currentTarget.dispatchEvent(
                new KeyboardEvent('keydown', { key: 'ArrowDown' })
              );
            } else {
              // Simulate arrow up key press
              e.currentTarget.dispatchEvent(
                new KeyboardEvent('keydown', { key: 'ArrowUp' })
              );
            }
          }}
          {...props}
        />
      </PopoverPrimitive.Portal>
    );
  }
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

const PopoverHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="popover-header"
    className={cn('flex flex-col gap-0.5', className)}
    {...props}
  />
));
PopoverHeader.displayName = 'PopoverHeader';

const PopoverTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, role = 'heading', 'aria-level': ariaLevel = 3, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="popover-title"
    role={role}
    aria-level={ariaLevel}
    className={cn('font-medium', className)}
    {...props}
  />
));
PopoverTitle.displayName = 'PopoverTitle';

const PopoverDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="popover-description"
    className={cn(typographyVariant({ variant: 'p2' }), 'text-text-secondary', className)}
    {...props}
  />
));
PopoverDescription.displayName = 'PopoverDescription';

export {
  Popover,
  PopoverAnchor,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
};
